import React, { useEffect, useRef, useState } from "react";
import Text from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import CircularProgress from "@material-ui/core/CircularProgress";
import axios from "api/configApi";
import { useSelector } from "react-redux";
import { reducerType } from "redux/reducers";
import { formatVolunteerTime } from "common/utils";

interface CertificatesTabProps {
  /** When set (e.g. Manage Volunteers Work), loads certificate for that user. */
  userId?: string;
}

type TargetMinutesState =
  | { status: "self" }
  | { status: "loading" }
  | { status: "ready"; minutes: number };

const CertificatesTab = React.memo((props: CertificatesTabProps) => {
  /// User by Admin and Volunteer Lead can see certificates for other users
  const { userId } = props;
  const classes = styles();
  const translate = useTranslation().t;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const userInfo = useSelector((state: reducerType) => state.auth.current_user);
  const [targetMinutesState, setTargetMinutesState] = useState<TargetMinutesState>(() =>
    userId ? { status: "loading" } : { status: "self" }
  );

  /** Avoid duplicate `/my-works/certificates` when effect re-runs for the same target user. */
  const oldUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!userId) {
      setTargetMinutesState({ status: "self" });
      return;
    }
    setTargetMinutesState({ status: "ready", minutes: 0 });
    // setTargetMinutesState({ status: "loading" });
    // axios
    //   .get("/my-works/statistics", { params: { userId } })
    //   .then((res) => {
    //     setTargetMinutesState({
    //       status: "ready",
    //       minutes: Number(res.data?.totalMinutesSpent) || 0,
    //     });
    //   })
    //   .catch(() => {
    //     setTargetMinutesState({ status: "ready", minutes: 0 });
    //   });
  }, [userId]);

  const volunteerMinutesForDisplay =
    targetMinutesState.status === "self"
      ? userInfo.volunteerMinutes ?? 0
      : targetMinutesState.status === "ready"
      ? targetMinutesState.minutes
      : null;

  const minutesLoading = Boolean(userId) && targetMinutesState.status === "loading";

  const volunteerHours =
    volunteerMinutesForDisplay === null
      ? null
      : Math.round((volunteerMinutesForDisplay / 60) * 100) / 100;

  useEffect(() => {
    if (volunteerHours === null) {
      return;
    }

    let objectUrl: string | null = null;

    const fetchCertificates = async () => {
      if (oldUserIdRef.current === userId) {
        return;
      }
      oldUserIdRef.current = userId;

      // Just check for self user
      if (!userId && volunteerHours < 4) {
        setPdfUrl(null);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await axios.get("/my-works/certificates", {
          params: userId ? { userId } : {},
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch (err) {
        setError(translate("SOMETHING_WENT_WRONG"));
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volunteerHours, userId]);

  const showSpinner = minutesLoading || loading;

  const renderBody = () => {
    if (showSpinner) {
      return (
        <div style={{ textAlign: "center", padding: 16 }}>
          <CircularProgress />
        </div>
      );
    }
    if (error) {
      return (
        <Text color="error" variant="body2">
          {error}
        </Text>
      );
    }
    if (!userId && volunteerHours !== null && volunteerHours < 4) {
      return (
        <Text variant="body2">
          {translate("CERTIFICATE_HOURS_AND_REQUIREMENT", {
            value: formatVolunteerTime(volunteerMinutesForDisplay ?? 0, translate),
          })}
        </Text>
      );
    }
    if (volunteerHours !== null && pdfUrl) {
      return (
        <div style={{ width: "100%", overflow: "auto" }}>
          <div style={{ transformOrigin: "top left" }}>
            <iframe
              src={pdfUrl}
              title="Certificates"
              style={{ width: "100%", height: "75vh", border: "none" }}
            />
          </div>
        </div>
      );
    }
    return (
      <Text variant="body2">
        {translate("NO_CERTIFICATES_AVAILABLE")}
      </Text>
    );
  };

  return <Container className={classes.root}>{renderBody()}</Container>;
});

export default CertificatesTab;
